
import React, { useState } from 'react';
import { X, Plus, User } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { PaperAuthor } from '@/types/paper';
import { Switch } from '@/components/ui/switch';

interface AuthorsInputProps {
  authors: PaperAuthor[];
  setAuthors: (authors: PaperAuthor[]) => void;
}

export const AuthorsInput: React.FC<AuthorsInputProps> = ({ authors, setAuthors }) => {
  const [newAuthorName, setNewAuthorName] = useState('');
  const [newAuthorInstitution, setNewAuthorInstitution] = useState('');
  const [isPlatformUser, setIsPlatformUser] = useState(false);
  const [error, setError] = useState('');

  const handleAddAuthor = () => {
    if (!newAuthorName.trim()) {
      setError('Author name is required');
      return;
    }

    // Check if an author with the same name already exists (case insensitive)
    const normalizedNewName = newAuthorName.trim().toLowerCase();
    const isDuplicate = authors.some(author => 
      author.name.toLowerCase() === normalizedNewName
    );

    if (isDuplicate) {
      setError('An author with this name already exists');
      return;
    }

    setError('');
    
    // Create a new author object
    const newAuthor: PaperAuthor = {
      id: `external-${Date.now()}`, // For non-platform users, use a timestamp-based ID
      name: newAuthorName.trim(),
      isPlatformUser: isPlatformUser,
      institution: newAuthorInstitution.trim() || undefined
    };
    
    // Add to authors array
    setAuthors([...authors, newAuthor]);
    
    // Reset form fields
    setNewAuthorName('');
    setNewAuthorInstitution('');
    setIsPlatformUser(false);
  };

  const handleRemoveAuthor = (indexToRemove: number) => {
    setAuthors(authors.filter((_, index) => index !== indexToRemove));
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Label className="text-base font-medium">Additional Authors</Label>
      </div>
      
      {/* Authors list */}
      {authors.length > 0 && (
        <div className="space-y-2 mb-4">
          {authors.map((author, index) => (
            <div key={index} className="flex items-center justify-between p-3 bg-muted/50 rounded-md">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <div>
                  <p className="text-sm font-medium">{author.name}</p>
                  {author.institution && (
                    <p className="text-xs text-muted-foreground">{author.institution}</p>
                  )}
                </div>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => handleRemoveAuthor(index)}
                aria-label="Remove author"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
      
      {/* Add author form */}
      <div className="space-y-3 p-4 border border-border rounded-md">
        <div className="space-y-2">
          <Label htmlFor="authorName">Author Name</Label>
          <Input
            id="authorName"
            value={newAuthorName}
            onChange={(e) => setNewAuthorName(e.target.value)}
            placeholder="Enter author name"
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="authorInstitution">Institution (optional)</Label>
          <Input
            id="authorInstitution"
            value={newAuthorInstitution}
            onChange={(e) => setNewAuthorInstitution(e.target.value)}
            placeholder="Enter institution name"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Switch 
            id="isPlatformUser" 
            checked={isPlatformUser} 
            onCheckedChange={setIsPlatformUser} 
          />
          <Label htmlFor="isPlatformUser">Is a platform user</Label>
        </div>
        
        {error && <p className="text-destructive text-sm">{error}</p>}
        
        <Button 
          type="button" 
          variant="outline" 
          className="w-full flex items-center gap-1.5"
          onClick={handleAddAuthor}
        >
          <Plus className="h-4 w-4" />
          Add Author
        </Button>
      </div>
    </div>
  );
};
